import React, {useContext, useState, useEffect} from 'react'
import { ElementContext } from '../ElementContext';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { getWhatsappApiTemplates, insertVariablesSelectElements, postWhatsappApiTemplates } from '../../helpers/whatsapp-api';
import '../../styles/wa-templates.css'

Modal.setAppElement('#root');

const WAPINewTemplateModal = () => {
    
    const { whatsAPIModalOpen, setWhatsAPIModalOpen, setWhatsappTemplates, selectedWACredentials, userConfiguration, setApprovedTemplates } = useContext(ElementContext)
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false)
    const [newTemplateValues, setNewTemplateValues] = useState({
        'tempName': '',
        'tempLanguage': '',
        'tempCategory': '',
        'tempHeader': '',
        'tempBody': '',
        'tempFooter': '',
    })
    const [insertedVar, setInsertedVar] = useState(0)
    const [firstPart, setFirstPart] = useState({ enabled: true, completed: false })

    useEffect(() => {
        if (whatsAPIModalOpen) {
            setFirstPart({ enabled: true, completed: false })
            setNewTemplateValues({
                'tempName': '',
                'tempLanguage': '',
                'tempCategory': '',
                'tempHeader': '',
                'tempBody': '',
                'tempFooter': '',
            })
            setInsertedVar(0)
            
            const insertedVarInputs = document.querySelector('#insertedVarInputs')
            if (insertedVarInputs) {
                insertedVarInputs.innerHTML = ''
            }
        }
    }, [whatsAPIModalOpen])

    const closeModal = () => {
        setFirstPart({ enabled: true, completed: false })
        setNewTemplateValues({
            'tempName': '',
            'tempLanguage': '',
            'tempCategory': '',
            'tempHeader': '',
            'tempBody': '',
            'tempFooter': '',
        })
        setInsertedVar(0)
        setWhatsAPIModalOpen(false)
    }

    const handleNewTemplateChange = async(e) => {
        const { name, value } = e.target;
        const { tempName, tempLanguage, tempCategory, tempHeader, tempBody, tempFooter } = newTemplateValues
        const newTemplate = { tempName, tempLanguage, tempCategory, tempHeader, tempBody, tempFooter }

        if( name === 'tempName' ){
            newTemplate[name] = await value.replace(/\s+/g, '_').toLowerCase().replace(/[^a-z0-9_]/g, '')
        } else if(name === 'tempBody'){
            // 🔍 DETECTAR VARIABLES EN EL TEXTO
            const matches = value.match(/\{\{\d+\}\}/g) || [];
            const maxVar = matches.length > 0 
                ? Math.max(...matches.map(m => parseInt(m.replace(/[{}]/g, '')))) 
                : 0;
            setInsertedVar(maxVar);
            
            newTemplate[name] = await value
        } else {
            newTemplate[name] = await value
        }

        if(newTemplate.tempName && newTemplate.tempLanguage && newTemplate.tempCategory){
            setFirstPart({ enabled: firstPart.enabled, completed: true })
        } else {
            setFirstPart({ enabled: firstPart.enabled, completed: false })
        }

        setNewTemplateValues(newTemplate)
    }

    const handleConfigurationPage = (e, enabled) => {
        e.preventDefault()
        setFirstPart({ enabled, completed: firstPart.completed })
    }

    const handleInsertVariable = async() => {
        if(insertedVar < 10){
            const newVar = insertedVar + 1
            const variable = ` {{${newVar}}}`;
            const finalBody = newTemplateValues.tempBody.trim() + variable;
            
            const { tempName, tempLanguage, tempCategory, tempHeader, tempFooter } = newTemplateValues
            const newTemplate = {
                tempName, tempLanguage, tempCategory, tempHeader, tempBody: finalBody, tempFooter
            }

            setNewTemplateValues(newTemplate)
            setInsertedVar(newVar)
            await document.querySelector('#tempBody').focus()
        }
    }

    // ✅ VALIDACIÓN MEJORADA DEL CONTENIDO
    const validateTemplateContent = (body, header, footer) => {
        const errors = []

        // 1. El body debe tener al menos 10 caracteres de contenido real
        const bodyWithoutVars = body.replace(/\{\{\d+\}\}/g, '').trim()
        if (bodyWithoutVars.length < 10) {
            errors.push('El cuerpo del mensaje debe tener al menos 10 caracteres de contenido significativo')
        }

        // 2. No permitir mensajes muy genéricos
        const genericWords = ['hola', 'test', 'prueba', 'plantilla']
        const bodyLower = bodyWithoutVars.toLowerCase()
        if (genericWords.some(word => bodyLower === word || bodyLower.includes(`${word} ${word}`))) {
            errors.push('El contenido no puede ser genérico. Debe incluir información específica del negocio')
        }

        // 3. Validar que tenga sentido empresarial
        if (bodyWithoutVars.split(' ').length < 3) {
            errors.push('El mensaje debe contener al menos 3 palabras con información relevante')
        }

        // 4. Header no debe ser muy corto si se incluye
        if (header && header.trim().length > 0 && header.trim().length < 5) {
            errors.push('El encabezado debe tener al menos 5 caracteres o déjalo vacío')
        }

        return errors
    }

    const handleSendNewTemplate = async(e) => {
        e.preventDefault()
        setLoading(true)
        
        try {
            const allWACredentials = userConfiguration.whatsappAPIcredentials
            const WACredentials = allWACredentials.find(cred => cred.phoneNumberId === selectedWACredentials)
            
            if (!WACredentials) {
                throw new Error('No se encontraron credenciales seleccionadas')
            }

            const { tempName, tempLanguage, tempCategory, tempHeader, tempBody, tempFooter } = newTemplateValues

            // ✅ VALIDACIONES BÁSICAS
            if (!tempName || !tempLanguage || !tempCategory) {
                Swal.fire(t('error'), 'Nombre, idioma y categoría son obligatorios', 'error')
                setLoading(false)
                return
            }

            if (!tempBody || !tempBody.trim()) {
                Swal.fire(t('error'), 'El cuerpo del mensaje es obligatorio', 'error')
                setLoading(false)
                return
            }

            // ✅ VALIDAR CONTENIDO SEGÚN REGLAS DE META
            const contentErrors = validateTemplateContent(tempBody, tempHeader, tempFooter)
            if (contentErrors.length > 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Contenido insuficiente',
                    html: `
                        <div class="text-start">
                            <p><strong>Meta requiere contenido más específico:</strong></p>
                            <ul>
                                ${contentErrors.map(err => `<li>${err}</li>`).join('')}
                            </ul>
                            <hr>
                            <p><small><strong>Ejemplos válidos:</strong></small></p>
                            <p><small>✅ "Hola, tu pedido #{{1}} está en camino. Llegaré aproximadamente el {{2}}."</small></p>
                            <p><small>✅ "Recordatorio: Tu cita con el Dr. {{1}} es mañana a las {{2}}."</small></p>
                            <p><small>❌ "hola" - Muy genérico</small></p>
                            <p><small>❌ "test test" - Sin contenido real</small></p>
                        </div>
                    `,
                    confirmButtonText: 'Entendido',
                    width: '600px'
                })
                setLoading(false)
                return
            }

            // ✅ SANITIZAR NOMBRE
            const sanitizedName = tempName
                .toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '')

            if (!/^[a-z0-9_]+$/.test(sanitizedName)) {
                Swal.fire(t('error'), 'El nombre solo puede contener letras minúsculas, números y guiones bajos', 'error')
                setLoading(false)
                return
            }

            // ✅ GENERAR EJEMPLOS FIJOS PARA META (sin mapeo de usuario)
            let varValues = []
            for(let i = 1; i <= insertedVar; i++){
                // Ejemplos genéricos que Meta aceptará
                const ejemplos = [
                    'Juan Pérez',           // {{1}}
                    '2025-12-31',          // {{2}}
                    'Pedido #12345',       // {{3}}
                    '15:30',               // {{4}}
                    'Dr. García',          // {{5}}
                    '$1,000',              // {{6}}
                    'Factura #001',        // {{7}}
                    'Buenos Aires',        // {{8}}
                    'Acme Corp',           // {{9}}
                    'Urgente'              // {{10}}
                ]
                varValues.push(ejemplos[i-1] || `Valor ${i}`)
            }

            // ✅ CONSTRUIR COMPONENTES
            const components = []

            // HEADER (solo si tiene contenido significativo)
            if (tempHeader && tempHeader.trim().length >= 5) {
                components.push({
                    "type": "HEADER",
                    "format": "TEXT",
                    "text": tempHeader.trim()
                })
            }

            // BODY (OBLIGATORIO)
            const bodyComponent = {
                "type": "BODY",
                "text": tempBody.trim()
            }
            
            // Solo agregar example si hay variables Y tienen valores
            if (varValues.length > 0) {
                bodyComponent.example = {
                    "body_text": [varValues]
                }
            }
            
            components.push(bodyComponent)

            // FOOTER (solo si tiene contenido)
            if (tempFooter && tempFooter.trim()) {
                components.push({
                    "type": "FOOTER",
                    "text": tempFooter.trim()
                })
            }

            // ✅ CREAR OBJETO FINAL
            const templateParams = {
                "name": sanitizedName,
                "language": tempLanguage,
                "category": tempCategory,
                "components": components
            }

            console.log('Enviando template a Meta:', JSON.stringify(templateParams, null, 2))

            // ✅ ENVIAR A META
            const newTemplateReq = await postWhatsappApiTemplates(WACredentials, templateParams)
            
            console.log('Respuesta de Meta:', newTemplateReq)

            // ✅ MANEJAR RESPUESTA
            if (newTemplateReq.id) {
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    html: `
                        <div class="text-start">
                            <p><strong>Plantilla creada exitosamente</strong></p>
                            <p><small>Estado: <strong>Pendiente de aprobación por Meta</strong></small></p>
                            <p><small>Nombre: <strong>${sanitizedName}</strong></small></p>
                            <hr>
                            <p><small>La revisión suele tomar de 15 minutos a 24 horas.</small></p>
                        </div>
                    `,
                    timer: 5000,
                    confirmButtonText: 'Entendido',

                })

                const whatsappApiTemplates = await getWhatsappApiTemplates(WACredentials)
                const allTemplates = whatsappApiTemplates.allTemplates || []
                const approvedTemplates = whatsappApiTemplates.approvedTemplates || []
                setWhatsappTemplates(allTemplates)
                setApprovedTemplates(approvedTemplates)
                
                closeModal()
                
            } else {
                const errorMsg = newTemplateReq.error?.message || 
                            newTemplateReq.error?.error_user_msg || 
                            newTemplateReq.error?.error_user_title ||
                            'Error desconocido'
                
                const errorDetails = newTemplateReq.error?.error_data?.details || ''
                
                console.error('❌ Error completo de Meta:', newTemplateReq)
                
                Swal.fire({
                    icon: 'error',
                    title: 'Meta rechazó la plantilla',
                    html: `
                        <div class="text-start">
                            <p><strong>Error:</strong> ${errorMsg}</p>
                            ${errorDetails ? `<p><small>${errorDetails}</small></p>` : ''}
                            <hr>
                            <p><small><strong>💡 Sugerencias:</strong></small></p>
                            <ul class="text-start" style="font-size: 0.85em;">
                                <li>Usa contenido específico del negocio, no genérico</li>
                                <li>El mensaje debe tener al menos 10 caracteres útiles</li>
                                <li>Evita palabras como "test", "prueba", "hola" solas</li>
                                <li>Include contexto empresarial real</li>
                            </ul>
                        </div>
                    `,
                    confirmButtonText: 'Entendido',
                    width: '600px'
                })
            }
            
        } catch (error) {
            console.error('❌ Error al crear template:', error)
            Swal.fire(t('error'), error.message || t('templateBadRequest'), 'error')
        } finally {
            setLoading(false)
        }
    }

    const handleCancelNewTemplate = (e) => {
        e.preventDefault()
        closeModal()
    }
    

  return (
        <Modal
            isOpen={whatsAPIModalOpen}
            closeTimeoutMS={ 200 }
            className="modal-new-template"
            overlayClassName="modal-fondo"
        >
            <h4 className='text-center text-mediumblue mb-0'>{t('newTemplate')}</h4>
            {
                firstPart.enabled ? (
                    <div className="d-flex justify-content-center">
                        <small className='text-lightblue'>
                            {t('step')} 1: {t('configureYourTemplate')}
                        </small>
                    </div>
                ) : (
                    <div className="d-flex justify-content-center">
                        <small className='text-lightblue'>
                            {t('step')} 2: {t('editTemplateContent')}
                        </small>
                    </div>
                )
            }
            <hr />

            <form id='newTemplateForm'>
                <div className='template-form'>
                    <div className={ firstPart.enabled ? 'template-form-first-part animate__animated animate__fadeInLeft' : 'd-none' }>
                        <div className="mb-3">
                            <label htmlFor="tempName" className="form-label template-labels mb-0">
                                {t('yourTemplateName')}
                            </label>
                            <input
                                autoComplete='off'
                                disabled={loading}
                                type="text"
                                className="form-control"
                                id="tempName"
                                name='tempName'
                                value={ newTemplateValues.tempName }
                                onChange={ handleNewTemplateChange }
                                placeholder="ej: confirmacion_pedido" />
                            <small className="form-text text-muted">Solo minúsculas, números y guiones bajos (_)</small>
                        </div>
                    
                        <div className="mb-3">
                            <label htmlFor="tempLanguage" className="form-label template-labels mb-0">
                                {t('selectTempLanguage')}
                            </label>
                            <select className="form-select form-select-sm" id="tempLanguage" name='tempLanguage' defaultValue={'default'} aria-label="Select language" disabled={loading} onChange={ handleNewTemplateChange } >
                                <option value="default" disabled>{t('selectLanguage')}</option>
                                <option value="es_ES">{t('spanish')}</option>
                                <option value="en_US">{t('english')}</option>
                            </select>
                        </div>
                    
                        <div className="mb-3">
                            <label className="form-label template-labels mb-2">
                                {t('selectTempCateg')}
                            </label>
                            <div className='d-flex align-items-start'>
                                <input type="radio" name='tempCategory' id="radio-marketing" value="MARKETING" className='tempCategory mt-1' onChange={ handleNewTemplateChange } />
                                <label htmlFor='radio-marketing' className='category-radio'>
                                    <span>{t('marketing')}</span>
                                    <small>
                                        {t('marketingDetails')}.
                                    </small>
                                </label>
                            </div>
                            <div className='d-flex align-items-start'>
                                <input type="radio" name='tempCategory' id="radio-transactional" value="UTILITY" className='tempCategory mt-1' onChange={ handleNewTemplateChange } />
                                <label htmlFor='radio-transactional' className='category-radio'>
                                    <span>Utilidad</span>
                                    <small>
                                        {t('transactionalDetails')}.
                                    </small>
                                </label>
                            </div>
                        </div>
                        
                        <div className='mt-4 d-flex justify-content-center'>
                            <button
                                disabled={loading}
                                className='btn btn-sm btn-91'
                                onClick={ handleCancelNewTemplate }
                            >
                                {t('cancel')}
                            </button>
                            
                            <button
                                disabled={ !firstPart.completed }
                                className='btn btn-sm btn-lightblue ms-3'
                                onClick={ (e) => handleConfigurationPage(e, false) }
                            >
                                {t('next')}
                            </button>
                        </div>
                    </div>

                    <div className={ !firstPart.enabled ? 'template-form-second-part animate__animated animate__fadeInRight' : 'd-none' }>
                        <div className="alert alert-info" role="alert">
                            <small>
                                <strong>Importante:</strong> Meta requiere contenido específico del negocio.
                                Evita mensajes genéricos como "hola" o "test". Incluye información útil para el usuario.
                            </small>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="tempHeader" className="form-label template-labels mb-0">
                                {t('header')} <span className="text-muted">(opcional)</span>
                            </label>
                            <input
                                autoComplete='off'
                                disabled={loading}
                                type="text"
                                className="form-control"
                                id="tempHeader"
                                name='tempHeader'
                                value={ newTemplateValues.tempHeader }
                                onChange={ handleNewTemplateChange }
                                placeholder="ej: Confirmación de pedido" />
                            <small className="form-text text-muted">Mínimo 5 caracteres o déjalo vacío</small>
                        </div>

                        <div className="mb-2">
                            <label htmlFor="tempBody" className="form-label template-labels mb-0">
                                {t('bodyMessage')} <span className="text-danger">*</span>
                            </label>
                            <textarea
                                disabled={loading}
                                className="form-control"
                                placeholder="ej: Hola, tu pedido #{{1}} está en preparación. Llegará aproximadamente el {{2}}."
                                id="tempBody"
                                name='tempBody'
                                value={ newTemplateValues.tempBody }
                                onChange={ handleNewTemplateChange }
                                rows="4">
                            </textarea>
                            <div className='d-flex justify-content-between align-items-center'>
                                <small className="form-text text-muted">Mínimo 10 caracteres con contenido significativo</small>
                                <small className='pointer text-primary' onClick={ handleInsertVariable }>
                                    + {t('insertVariable')}
                                </small>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="tempFooter" className="form-label template-labels mb-0">
                                {t('footerMessage')} <span className="text-muted">(opcional)</span>
                            </label>
                            <input
                                autoComplete='off'
                                disabled={loading}
                                type="text"
                                className="form-control"
                                id="tempFooter"
                                name='tempFooter'
                                value={ newTemplateValues.tempFooter }
                                onChange={ handleNewTemplateChange }
                                placeholder="ej: Equipo de Soporte" />
                        </div>
                        
                        <div className='d-flex justify-content-between button-agrupation'>
                            <div>
                                <button
                                    disabled={loading}
                                    className='btn btn-sm btn-mediumblue'
                                    onClick={ handleSendNewTemplate }
                                >
                                    {
                                        loading ? (
                                            <>
                                            <span className='text-light'>{t('processing')}...</span>
                                            <div className="spinner-border text-light loading-button ms-2" role="status">
                                                <span className="visually-hidden">...</span>
                                            </div>
                                            </>
                                        ) : t('createTemplate')
                                    }
                                </button>
                            </div>

                            <div className='btn-group'>
                                <button
                                    disabled={loading}
                                    className='btn btn-sm btn-lightblue'
                                    onClick={ (e) => handleConfigurationPage(e, true) }
                                >
                                    {t('back')}
                                </button>
                                
                                <button
                                    disabled={loading}
                                    className='btn btn-sm btn-91'
                                    onClick={ handleCancelNewTemplate }
                                >
                                    {t('cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
  )
}

export default WAPINewTemplateModal